import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  makeStyles,
  Paper,
  Typography,
  Modal,
  Button,
} from "@material-ui/core";
import SendIcon from "@material-ui/icons/Send";
import InsertEmoticonIcon from "@material-ui/icons/InsertEmoticon"; // Ícone de emoji
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Estilo do editor
import EmojiPicker from "emoji-picker-react"; // Seletor de emojis
import GetAppIcon from "@material-ui/icons/GetApp"; // Ícone de download

import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";
import api from "../../services/api";

import waBackground from '../../assets/wa-background.png'; // Importe a imagem de fundo

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    flex: 1,
    overflow: "hidden",
    height: "100%",
    backgroundColor: theme.palette.background.default,
  },
  messageList: {
    position: "relative",
    overflowY: "auto",
    flex: 1,
    padding: "20px",
    ...theme.scrollbarStyles,
    backgroundImage: `url(${waBackground})`,
    backgroundSize: "400px",
    backgroundRepeat: "repeat",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: theme.palette.type === 'light' 
        ? "rgba(255, 255, 255, 0.9)" 
        : "rgba(15, 23, 42, 0.9)",
      pointerEvents: "none",
    },
  },
  inputArea: {
    position: "relative",
    backgroundColor: theme.palette.background.paper,
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  editor: {
    flex: 1,
    backgroundColor: theme.palette.type === 'light' ? "#f8fafc" : "rgba(255, 255, 255, 0.05)",
    borderRadius: "24px",
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s ease",
    overflow: "hidden",
    "&:focus-within": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 4px ${theme.palette.primary.main}15`,
    },
    "& .ql-toolbar": {
      display: "none", // Simplificando para versão interna
    },
    "& .ql-container": {
      border: "none !important",
      height: "auto",
      minHeight: "44px",
      maxHeight: "150px",
    },
    "& .ql-editor": {
      padding: "10px 20px",
      fontSize: "15px",
    }
  },
  iconButton: {
    padding: "10px",
    borderRadius: "12px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === 'light' 
        ? "hsla(270, 80%, 60%, 0.1)" 
        : "hsla(270, 80%, 60%, 0.2)",
      transform: "scale(1.05)",
    },
  },
  sendButton: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  boxLeft: {
    padding: "12px 16px",
    margin: "8px 0",
    position: "relative",
    backgroundColor: theme.palette.type === 'light' ? "#fff" : "#1e293b",
    color: theme.palette.text.primary,
    maxWidth: "80%",
    borderRadius: "18px 18px 18px 4px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    zIndex: 1,
    alignSelf: "flex-start",
  },
  boxRight: {
    padding: "12px 16px",
    margin: "8px 0 8px auto",
    position: "relative",
    backgroundColor: theme.palette.type === 'light' 
      ? "hsla(270, 80%, 60%, 0.12)" 
      : "hsla(270, 80%, 60%, 0.25)",
    color: theme.palette.text.primary,
    maxWidth: "80%",
    borderRadius: "18px 18px 4px 18px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    zIndex: 1,
    alignSelf: "flex-end",
  },
  senderName: {
    fontSize: "12px",
    fontWeight: "700",
    color: theme.palette.primary.main,
    marginBottom: "4px",
  },
  messageTime: {
    fontSize: "10px",
    opacity: 0.6,
    marginTop: "4px",
    textAlign: "right",
  },
  emojiPicker: {
    position: "absolute",
    bottom: "100%",
    left: "24px",
    marginBottom: "10px",
    zIndex: 10,
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backdropFilter: "blur(4px)",
  },
  modalContent: {
    backgroundColor: theme.palette.background.paper,
    padding: "24px",
    borderRadius: "20px",
    textAlign: "center",
    maxWidth: "90vw",
    boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
  },
  modalImage: {
    maxWidth: "100%",
    maxHeight: "75vh",
    borderRadius: "12px",
    marginBottom: "20px",
  },
}));

export default function ChatMessages({
  chat,
  messages,
  handleSendMessage,
  handleLoadMore,
  scrollToBottomRef,
  pageInfo,
  loading,
}) {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const { datetimeToClient } = useDate();
  const baseRef = useRef();

  const [contentMessage, setContentMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false); // Estado para controlar a exibição do seletor de emojis
  const [selectedImage, setSelectedImage] = useState(null); // Estado para controlar a imagem selecionada para ampliar

  const scrollToBottom = () => {
    if (baseRef.current) {
      baseRef.current.scrollIntoView({});
    }
  };

  const unreadMessages = (chat) => {
    if (chat !== undefined) {
      const currentUser = chat.users.find((u) => u.userId === user.id);
      return currentUser.unreads > 0;
    }
    return 0;
  };

  useEffect(() => {
    if (unreadMessages(chat) > 0) {
      try {
        api.post(`/chats/${chat.id}/read`, { userId: user.id });
      } catch (err) {}
    }
    scrollToBottomRef.current = scrollToBottom;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = (e) => {
    const { scrollTop } = e.currentTarget;
    if (!pageInfo.hasMore || loading) return;
    if (scrollTop < 600) {
      handleLoadMore();
    }
  };

  const handleSend = () => {
    if (contentMessage.trim() !== "") {
      handleSendMessage(contentMessage);
      setContentMessage("");
    }
  };

  // Função para adicionar emoji ao conteúdo da mensagem
  const handleEmojiClick = (emojiObject) => {
    setContentMessage((prevMessage) => prevMessage + emojiObject.emoji);
    setShowEmojiPicker(false); // Fecha o seletor de emojis após a seleção
  };

  // Função para abrir a imagem no modal
  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
  };

  // Função para fechar o modal
  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  // Função para baixar a imagem
  const handleDownloadImage = () => {
    if (selectedImage) {
      const link = document.createElement("a");
      link.href = selectedImage;
      link.download = "image.png"; // Nome do arquivo para download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Configuração da barra de ferramentas do editor
  const modules = {
    toolbar: [
      ["bold", "italic", "underline", "strike"], // Negrito, Itálico, Sublinhado, Taxado
      ["image"], // Inserir imagem
    ],
  };

  // Efeito para adicionar eventos de clique nas imagens após o render
  useEffect(() => {
    const images = document.querySelectorAll(".messageContent img");
    images.forEach((img) => {
      img.style.cursor = "pointer";
      img.addEventListener("click", () => handleImageClick(img.src));
    });

    return () => {
      images.forEach((img) => {
        img.removeEventListener("click", () => handleImageClick(img.src));
      });
    };
  }, [messages]);

  return (
    <Paper className={classes.mainContainer}>
      <Box display="flex" flexDirection="column" className={classes.messageList}>
        {Array.isArray(messages) &&
          messages.map((item, key) => {
            const isMe = item.senderId === user.id;
            return (
              <Box key={key} className={isMe ? classes.boxRight : classes.boxLeft}>
                <Typography className={classes.senderName}>
                  {item.sender.name}
                </Typography>
                <div
                  className="messageContent"
                  dangerouslySetInnerHTML={{ __html: item.message }}
                />
                <Typography className={classes.messageTime}>
                  {datetimeToClient(item.createdAt)}
                </Typography>
              </Box>
            );
          })}
        <div ref={baseRef}></div>
      </Box>
      <div className={classes.inputArea}>
        <IconButton
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          className={classes.iconButton}
        >
          <InsertEmoticonIcon color="primary" />
        </IconButton>

        {showEmojiPicker && (
          <div className={classes.emojiPicker}>
            <EmojiPicker onEmojiClick={handleEmojiClick} />
          </div>
        )}

        <ReactQuill
          value={contentMessage}
          onChange={setContentMessage}
          className={classes.editor}
          modules={{ toolbar: false }} // Desativa toolbar no chat interno para simplificar
          placeholder="Digite sua mensagem..."
        />

        <IconButton
          className={`${classes.iconButton} ${classes.sendButton}`}
          onClick={handleSend}
          disabled={!contentMessage.trim()}
        >
          <SendIcon />
        </IconButton>
      </div>

      {/* Modal para exibir a imagem ampliada */}
      <Modal
        open={!!selectedImage}
        onClose={handleCloseModal}
        className={classes.modal}
      >
        <div className={classes.modalContent}>
          <img
            src={selectedImage}
            alt="Ampliada"
            className={classes.modalImage}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={handleDownloadImage}
            style={{
              color: "white",
              backgroundColor: "#8E2DE2",
              boxShadow: "none",
              borderRadius: 0,
            }}
          >
            Baixar Imagem
          </Button>
        </div>
      </Modal>
    </Paper>
  );
}